import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BuildingOfficeIcon,
  UserIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import PageHeader from '../../components/PageHeader'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  INITIAL_IR_CAPTURE_FORM,
  activityCategoriesFromCatalog,
  selectionsForCategory,
  validateIrCapture,
  buildIrCapturePayload,
  labelForOption,
} from './irCaptureHelpers'

const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm'
const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-4 shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2">
        {Icon && <Icon className="h-5 w-5 text-psg-teal shrink-0" />}
        {title}
      </h2>
      {children}
    </section>
  )
}

function OptionChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded border transition ${
        active
          ? 'bg-psg-teal text-white border-psg-teal'
          : 'bg-white text-gray-800 border-gray-300 hover:border-psg-teal/50'
      }`}
    >
      {children}
    </button>
  )
}

function scrollAppMainToTop(behavior = 'smooth') {
  const main = document.querySelector('main.flex-1.overflow-y-auto')
  if (main) {
    main.scrollTo({ top: 0, behavior })
    return
  }
  window.scrollTo({ top: 0, behavior })
}

export default function IncidentRegisterCapture() {
  const pageTopRef = useRef(null)
  const successBannerRef = useRef(null)
  const [venues, setVenues] = useState([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalog, setCatalog] = useState({ reportCategories: [], formOptions: {} })
  const [form, setForm] = useState({ ...INITIAL_IR_CAPTURE_FORM })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          const mapped = j.data.map((v) => ({
            id: String(v.venue_id || v.id || '').toLowerCase(),
            name: v.name,
            code: v.code,
          }))
          setVenues(mapped)
          setForm((p) => (p.venueId ? p : { ...p, venueId: mapped[0].id }))
        }
      } catch (_) {}
    })()
  }, [])

  const loadCatalog = useCallback(async (vid) => {
    if (!vid) return
    setCatalogLoading(true)
    try {
      const qs = `?venue_id=${encodeURIComponent(vid)}`
      const res = await authFetch(getLocalApiUrl(`/api/mobile/ir-catalog${qs}`))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setCatalog(data.data || { reportCategories: [], formOptions: {} })
    } catch (e) {
      setError(e.message || 'Failed to load incident register catalog')
      setCatalog({ reportCategories: [], formOptions: {} })
    } finally {
      setCatalogLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!form.venueId) return
    loadCatalog(form.venueId)
  }, [form.venueId, loadCatalog])

  const activityCategories = useMemo(
    () => activityCategoriesFromCatalog(catalog),
    [catalog]
  )

  const selections = useMemo(
    () => selectionsForCategory(activityCategories, form.reportCategory),
    [activityCategories, form.reportCategory]
  )

  const selectedEntry = useMemo(
    () => selections.find((s) => s.id === form.selectionId) || null,
    [selections, form.selectionId]
  )

  const subOptions = selectedEntry?.subSelections || []
  const formOptions = catalog.formOptions || {}

  const updateForm = useCallback((patch) => {
    setForm((p) => ({ ...p, ...patch }))
  }, [])

  const toggleInList = (key, id) => {
    setForm((p) => {
      const list = p[key]
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
      return { ...p, [key]: next }
    })
  }

  const resetAfterSuccess = useCallback(() => {
    setForm({ ...INITIAL_IR_CAPTURE_FORM, venueId: form.venueId })
  }, [form.venueId])

  useEffect(() => {
    if (!success) return
    requestAnimationFrame(() => {
      scrollAppMainToTop('smooth')
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      successBannerRef.current?.focus?.({ preventScroll: true })
    })
  }, [success])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    const validationError = validateIrCapture(form, { subOptions })
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    try {
      const body = buildIrCapturePayload(form)
      const res = await authFetch(getLocalApiUrl('/api/mobile/ir-reports'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setSuccess(`Incident register report ${data.data?.id || ''} recorded`.trim())
      resetAfterSuccess()
    } catch (err) {
      setError(err.message || 'Failed to create report')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div ref={pageTopRef} className="max-w-4xl mx-auto px-4 py-6 scroll-mt-4">
      <PageHeader
        title="Incident Register Capture"
        description="Single-page capture for web and floor — separate from UAR (AML)."
        actions={
          <Link
            to="/compliance-safe/incident-register"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to list
          </Link>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">{error}</div>
        )}
        {success && (
          <div
            ref={successBannerRef}
            tabIndex={-1}
            role="status"
            className="p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-100 outline-none"
          >
            {success}
          </div>
        )}

        <Section title="Venue & category" icon={BuildingOfficeIcon}>
          {catalogLoading && (
            <p className="text-xs text-gray-500">Loading incident register catalog…</p>
          )}

          <div>
            <label className={labelClass}>Venue *</label>
            <select
              required
              value={form.venueId}
              onChange={(e) => {
                updateForm({
                  venueId: e.target.value,
                  reportCategory: '',
                  selectionId: '',
                  subSelectionIds: [],
                })
              }}
              className={inputClass}
            >
              <option value="">— Select venue —</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.code ? ` (${v.code})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Report category *</label>
            <select
              required
              value={form.reportCategory}
              onChange={(e) => {
                updateForm({
                  reportCategory: e.target.value,
                  selectionId: '',
                  subSelectionIds: [],
                })
              }}
              className={inputClass}
            >
              <option value="">— Select category —</option>
              {activityCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.label || c.id}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Incident type *</label>
            <select
              required
              value={form.selectionId}
              disabled={!form.reportCategory}
              onChange={(e) => {
                updateForm({ selectionId: e.target.value, subSelectionIds: [] })
              }}
              className={inputClass}
            >
              <option value="">— Select type —</option>
              {selections.map((s) => (
                <option key={s.id} value={s.id}>{s.selectionLabel || s.label || s.id}</option>
              ))}
            </select>
          </div>

          {subOptions.length > 0 && (
            <div>
              <label className={labelClass}>Warning signs (select all that apply) *</label>
              <div className="flex flex-wrap gap-2">
                {subOptions.map((opt) => (
                  <OptionChip
                    key={opt.id}
                    active={form.subSelectionIds.includes(opt.id)}
                    onClick={() => toggleInList('subSelectionIds', opt.id)}
                  >
                    {labelForOption(opt)}
                  </OptionChip>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Patron" icon={UserIcon}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Patron type</label>
              <select
                value={form.patronKind}
                onChange={(e) => updateForm({ patronKind: e.target.value })}
                className={inputClass}
              >
                <option value="">Not specified</option>
                {(formOptions.patronKinds || []).map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Membership number</label>
              <input
                type="text"
                value={form.membershipNumber}
                onChange={(e) => updateForm({ membershipNumber: e.target.value })}
                placeholder="Optional"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Patron name</label>
            <input
              type="text"
              value={form.patronName}
              onChange={(e) => updateForm({ patronName: e.target.value })}
              placeholder="Optional"
              className={inputClass}
            />
          </div>
        </Section>

        <Section title="Incident details" icon={ExclamationTriangleIcon}>
          <div>
            <label className={labelClass}>Location (EGM / zone) *</label>
            <input
              type="text"
              required
              value={form.locationDetail}
              onChange={(e) => updateForm({ locationDetail: e.target.value })}
              placeholder="e.g. EGM-089"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Incident description &amp; assessment *</label>
            <textarea
              required
              rows={4}
              value={form.assessmentText}
              onChange={(e) => updateForm({ assessmentText: e.target.value })}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Welfare check performed *</label>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Welfare check performed">
              {[
                { id: 'true', label: 'Yes' },
                { id: 'false', label: 'No' },
              ].map((opt) => (
                <OptionChip
                  key={opt.id}
                  active={form.welfarePerformed === opt.id}
                  onClick={() => updateForm({ welfarePerformed: opt.id })}
                >
                  {opt.label}
                </OptionChip>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Welfare check notes</label>
            <textarea
              rows={2}
              value={form.welfareNotes}
              onChange={(e) => updateForm({ welfareNotes: e.target.value })}
              placeholder="Optional"
              className={inputClass}
            />
          </div>
        </Section>

        <Section title="Actions taken" icon={ClipboardDocumentCheckIcon}>
          <div>
            <label className={labelClass}>Assistance provided</label>
            <div className="flex flex-wrap gap-2">
              {(formOptions.assistanceProvided || []).map((opt) => (
                <OptionChip
                  key={opt.id}
                  active={form.assistanceProvided.includes(opt.id)}
                  onClick={() => toggleInList('assistanceProvided', opt.id)}
                >
                  {labelForOption(opt)}
                </OptionChip>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Assistance notes</label>
            <textarea
              rows={2}
              value={form.assistanceNotes}
              onChange={(e) => updateForm({ assistanceNotes: e.target.value })}
              placeholder="Optional"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Patron stage of change</label>
            <div className="flex flex-wrap gap-2">
              {(formOptions.stageOfChange || []).map((opt) => (
                <OptionChip
                  key={opt.id}
                  active={form.stageOfChange.includes(opt.id)}
                  onClick={() => toggleInList('stageOfChange', opt.id)}
                >
                  {opt.group ? `${opt.group}: ${labelForOption(opt)}` : labelForOption(opt)}
                </OptionChip>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Further actions</label>
            <div className="flex flex-wrap gap-2">
              {(formOptions.furtherActions || []).map((opt) => (
                <OptionChip
                  key={opt.id}
                  active={form.furtherActions.includes(opt.id)}
                  onClick={() => toggleInList('furtherActions', opt.id)}
                >
                  {labelForOption(opt)}
                </OptionChip>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.printBreakVoucher}
              onChange={(e) => updateForm({ printBreakVoucher: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="font-medium text-gray-900">Print break in play voucher</span>
          </label>
        </Section>

        <div className="flex justify-end pt-2 pb-8">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Recording…' : 'Record incident'}
          </button>
        </div>
      </form>
    </div>
  )
}
